import { useState, useEffect } from 'react'
import { Box, Flex, Text, Grid, Spinner, Modal, useT } from '@minetrace/ui'
import {
  LuLandmark as Landmark,
  LuGlobe as Globe,
  LuRefreshCw as Refresh,
} from 'react-icons/lu'
import { getUtmZoneFromGeoJson, latLngToUtm } from '../utils/utmConverter'
import PolygonEditor, { type PolygonEditorValue } from './PolygonEditor'

interface GeoreferenceModalProps {
  open: boolean
  onClose: () => void
  miningRightName: string
  ubigeoCode: string | null
  existingGeoJson: string | null
  /** Zona UTM persistida del derecho (fuente de verdad). Si es null, se deriva de la geometría/ubigeo. */
  existingUtmZone?: number | null
  onSave: (vertices: { northing: number; easting: number }[], utmZone: number) => Promise<void>
  onZoneChange?: (zone: number) => void
  /** Contorno oficial del derecho minero (INGEMMET): referencia de fondo para dibujar la concesión. */
  officialGeoJson?: string | null
}

const GeoreferenceModal = ({ open, onClose, miningRightName, ubigeoCode, existingGeoJson, existingUtmZone, onSave, onZoneChange, officialGeoJson }: GeoreferenceModalProps) => {
  const { t } = useT()
  const [editorValue, setEditorValue] = useState<PolygonEditorValue>(() => ({
    vertices: [],
    utmZone: existingUtmZone ?? getUtmZoneFromGeoJson(existingGeoJson ?? officialGeoJson ?? null, ubigeoCode),
    newNorthing: '',
    newEasting: '',
  }))
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    const defaultZone = existingUtmZone ?? getUtmZoneFromGeoJson(existingGeoJson ?? officialGeoJson ?? null, ubigeoCode)
    let loaded: { northing: string; easting: string }[] = []
    if (existingGeoJson) {
      try {
        const geo = JSON.parse(existingGeoJson)
        const coords: number[][] = geo.type === 'Polygon' ? geo.coordinates[0] : []
        const points = coords.length > 1 && coords[0][0] === coords[coords.length - 1][0] && coords[0][1] === coords[coords.length - 1][1]
          ? coords.slice(0, -1)
          : coords
        loaded = points.map((c) => {
          const { northing, easting } = latLngToUtm(c[1], c[0], defaultZone)
          return { northing: String(northing), easting: String(easting) }
        })
      } catch {
        loaded = []
      }
    }
    setEditorValue({ vertices: loaded, utmZone: defaultZone, newNorthing: '', newEasting: '' })
  }, [open, existingGeoJson, ubigeoCode, existingUtmZone, officialGeoJson])

  const handleEditorChange = (next: PolygonEditorValue) => {
    if (next.utmZone !== editorValue.utmZone) onZoneChange?.(next.utmZone)
    setEditorValue(next)
  }

  const isIncomplete = editorValue.vertices.length < 3

  const handleSave = async () => {
    if (isIncomplete) return
    setSaving(true)
    try {
      await onSave(
        editorValue.vertices.map((v) => ({ northing: parseInt(v.northing), easting: parseInt(v.easting) })),
        editorValue.utmZone,
      )
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('shared.georeference.title')}
      subtitle={t('shared.georeference.subtitle')}
      size="xl"
      footer={
        <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4} w="100%">
          <Box bg="white" borderRadius="xl" border="1px solid #e8dfd1" p={4}>
            <Flex alignItems="center" gap={2} mb={2}>
              <Landmark size={16} color="#2a2622" />
              <Text tid="shared.georeference.ruleClosureTitle" fontSize="sm" fontWeight="700" color="#2a2622" />
            </Flex>
            <Text tid="shared.georeference.ruleClosureText" fontSize="xs" color="#857b71" />
          </Box>
          <Box bg="white" borderRadius="xl" border="1px solid #e8dfd1" p={4}>
            <Flex alignItems="center" gap={2} mb={2}>
              <Globe size={16} color="#2a2622" />
              <Text tid="shared.georeference.utmGridTitle" fontSize="sm" fontWeight="700" color="#2a2622" />
            </Flex>
            <Text tid="shared.georeference.utmGridText" values={{ zone: editorValue.utmZone }} fontSize="xs" color="#857b71" />
          </Box>
          <Box bg="white" borderRadius="xl" border="1px solid #e8dfd1" p={4}>
            <Flex alignItems="center" gap={2} mb={2}>
              <Refresh size={16} color="#2a2622" />
              <Text tid="shared.georeference.syncTitle" fontSize="sm" fontWeight="700" color="#2a2622" />
            </Flex>
            <Text tid="shared.georeference.syncText" fontSize="xs" color="#857b71" />
          </Box>
        </Grid>
      }
    >
      {/* Breadcrumb + incomplete badge */}
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Text tid="shared.georeference.breadcrumb" values={{ name: miningRightName }} fontSize="xs" color="#b5aa9d" fontWeight="600" textTransform="uppercase" letterSpacing="0.05em" />
        {isIncomplete && (
          <Box bg="#fff3cd" color="#856404" px={4} py={2} borderRadius="full" fontSize="xs" fontWeight="700" whiteSpace="nowrap">
            {t('shared.georeference.incompleteBadge')}
          </Box>
        )}
      </Flex>

      {officialGeoJson && (
        <Box mb={4} px={4} py={3} borderRadius="lg" bg="#f9eccf" border="1px solid #e8c98a" fontSize="xs" color="#5c4210" lineHeight="1.6">
          {t('shared.georeference.officialHint')}
        </Box>
      )}

      <PolygonEditor value={editorValue} onChange={handleEditorChange} referenceGeoJson={officialGeoJson} />

      {/* Action buttons */}
      <Flex gap={3} justifyContent="center" mt={4}>
        {editorValue.vertices.length > 0 && (
          <Box as="button" type="button" display="flex" alignItems="center" justifyContent="center"
            px={5} h="42px" borderRadius="lg" fontSize="sm" fontWeight="600"
            border="1px solid #e8dfd1" color="#4a5568"
            cursor="pointer" _hover={{ bg: '#faf6f0' }}
            onClick={() => setEditorValue({ ...editorValue, vertices: [] })}>
            {t('shared.georeference.discard')}
          </Box>
        )}
        <Box
          as="button" type="button" display="flex" alignItems="center" justifyContent="center" gap={2}
          px={6} h="42px" borderRadius="lg" fontSize="sm" fontWeight="700"
          bg={isIncomplete ? '#d3c6b2' : '#1d6664'} color="white"
          cursor={isIncomplete || saving ? 'not-allowed' : 'pointer'}
          _hover={isIncomplete ? {} : { bg: '#085a30' }}
          onClick={handleSave} disabled={isIncomplete || saving}
        >
          {saving && <Spinner size="sm" />}
          {t('shared.georeference.savePolygon')}
        </Box>
      </Flex>
    </Modal>
  )
}

export default GeoreferenceModal
